import { useMemo } from "react";
import { AxiosError } from "axios";
import { useMutation } from "react-query";
import catalog from "@granular/fabric-catalog";

import activities from "../../../redux/activities";
import { Member } from "../../../models/members";
import { UpdatePermissionsRequest } from "../../../models/permissions";
import { MutationEnvelope, ensureMutationState } from "../../../services/query";
import { getAuthToken } from "../../../services/granularAxios";
import operationsService from "../../../services/operations";
import invitesService from "../../../services/invites";
import { ensureValue } from "../../../services/util";

import { useMaybeUserId } from "../userId";
import {
  useMaybeCurrentOperationId,
  useInvalidateCurrentOperation,
} from "../operations";

import { useInvalidateMembers } from "./members.invalidate";
import { useSetMembersGlobalActivityAndStatus } from "./members.globalStatus";
import { useMaybeFabricFeatureFlag } from "../featureFlags";

// #####################
// ## useRemoveMember ##
// #####################

export type ResultOfUseRemoveMember = MutationEnvelope<string, null>;
export type UseRemoveMember = () => ResultOfUseRemoveMember;

/**
 * Use the `mutate` function returned from this hook to remove a member from the current operation.
 * This will trigger an invalidation of the members data.
 *
 * The `data`, `error`, and `status` properties returned from this hook will reflect
 * the state of the mutation.
 */
export const useRemoveMember: UseRemoveMember = () => {
  const invalidateMembers = useInvalidateMembers();
  const catalogFlag = useMaybeFabricFeatureFlag({
    key: "roles-api",
  });

  const maybeUserId = useMaybeUserId();
  const maybeOperationId = useMaybeCurrentOperationId();

  const setMembersGlobalActivityAndStatus =
    useSetMembersGlobalActivityAndStatus();

  const { data, error, status, mutateAsync } = useMutation<
    null,
    AxiosError,
    string
  >(
    async (memberId) => {
      const userId = ensureValue(maybeUserId, "userId");
      const operationId = ensureValue(maybeOperationId, "operationId");

      await operationsService.removeMemberFromOperation(
        {
          authToken: getAuthToken(),
          userId,
          operationId,
          memberId,
        },
        catalogFlag,
      );

      return null;
    },
    {
      onMutate: () => {
        setMembersGlobalActivityAndStatus({
          activity: activities.deleteMember,
          status: "loading",
        });
      },
      onSuccess: async () => {
        await invalidateMembers();

        setMembersGlobalActivityAndStatus({
          activity: activities.deleteMember,
          status: "success",
        });
      },
      onError: () => {
        setMembersGlobalActivityAndStatus({
          activity: activities.deleteMember,
          status: "error",
        });
      },
    },
  );

  const mutate = mutateAsync;

  return useMemo(
    () => ({
      ...ensureMutationState({ data, error, status }),
      mutate,
    }),
    [data, error, status, mutate],
  );
};

// #############################
// ## useRemoveCortevaMembers ##
// #############################

export type PayloadOfUseRemoveCortevaMembers = {
  copyOperation: boolean;
  removalReason: string;
};
export type ResultOfUseRemoveCortevaMembers = MutationEnvelope<
  PayloadOfUseRemoveCortevaMembers,
  null
>;
export type UseRemoveCortevaMembers = () => ResultOfUseRemoveCortevaMembers;

/**
 * Use the `mutate` function returned from this hook to remove all Corteva members from the current operation.
 * This will trigger an invalidation of the current operation data and the members data.
 *
 * The `data`, `error`, and `status` properties returned from this hook will reflect
 * the state of the mutation.
 */
export const useRemoveCortevaMembers: UseRemoveCortevaMembers = () => {
  const invalidateMembers = useInvalidateMembers();
  const invalidateCurrentOperation = useInvalidateCurrentOperation();
  const catalogFlag = useMaybeFabricFeatureFlag({
    key: "roles-api",
  });

  const maybeUserId = useMaybeUserId();
  const maybeOperationId = useMaybeCurrentOperationId();

  const setMembersGlobalActivityAndStatus =
    useSetMembersGlobalActivityAndStatus();

  const { data, error, status, mutateAsync } = useMutation<
    null,
    AxiosError,
    PayloadOfUseRemoveCortevaMembers
  >(
    async (payload) => {
      const userId = ensureValue(maybeUserId, "userId");
      const operationId = ensureValue(maybeOperationId, "operationId");

      if (catalogFlag) {
        const catalogConfig = {
          // IMPORTANT: We are not passing the authToken as part of the queryKey
          // because we don't want to invalidate our data on auth token change.
          // The auth token can and will change throughout the lifetime of the app,
          // but that should not invalidate our data.
          getAuthToken,
          getOperationId: () => operationId,
        };
        await catalog.roles.deleteCortevaUsers(
          operationId,
          {
            allow_copy_operation: payload.copyOperation,
            removal_reason: payload.removalReason,
          },
          catalogConfig,
        );
      } else {
        await invitesService.removeCortevaMembersFromOperation({
          authToken: getAuthToken(),
          userId,
          operationId,
          copyOperation: payload.copyOperation,
          removalReason: payload.removalReason,
        });
      }

      return null;
    },
    {
      onMutate: () => {
        setMembersGlobalActivityAndStatus({
          activity: activities.removeCortevaMembers,
          status: "loading",
        });
      },
      onSuccess: async () => {
        await Promise.all([
          invalidateMembers(),
          // Also invalidate the current operation because the `settings` property
          // of the current operation data is also updated by this request.
          invalidateCurrentOperation(),
        ]);

        setMembersGlobalActivityAndStatus({
          activity: activities.removeCortevaMembers,
          status: "success",
        });
      },
      onError: () => {
        setMembersGlobalActivityAndStatus({
          activity: activities.removeCortevaMembers,
          status: "error",
        });
      },
    },
  );

  const mutate = mutateAsync;

  return useMemo(
    () => ({
      ...ensureMutationState({ data, error, status }),
      mutate,
    }),
    [data, error, status, mutate],
  );
};

// ################################
// ## useUpdateMemberPermissions ##
// ################################

export type PayloadOfUseUpdateMemberPermissions = {
  member: Member;
  permissions: UpdatePermissionsRequest;
};
export type ResultOfUseUpdateMemberPermissions = MutationEnvelope<
  PayloadOfUseUpdateMemberPermissions,
  null
>;
export type UseUpdateMemberPermissions =
  () => ResultOfUseUpdateMemberPermissions;

/**
 * Use the `mutate` function returned from this hook to update member permissions within the current operation.
 * This will trigger an invalidation of the current operation data or the members data,
 * depending on whether the member is the current user or not.
 *
 * The `data`, `error`, and `status` properties returned from this hook will reflect
 * the state of the mutation.
 */
export const useUpdateMemberPermissions: UseUpdateMemberPermissions = () => {
  const invalidateMembers = useInvalidateMembers();
  const invalidateCurrentOperation = useInvalidateCurrentOperation();

  const maybeUserId = useMaybeUserId();
  const maybeOperationId = useMaybeCurrentOperationId();
  const catalogFlag = useMaybeFabricFeatureFlag({
    key: "roles-api",
  });

  const setMembersGlobalActivityAndStatus =
    useSetMembersGlobalActivityAndStatus();

  const { data, error, status, mutateAsync } = useMutation<
    null,
    AxiosError,
    PayloadOfUseUpdateMemberPermissions
  >(
    async (payload) => {
      const userId = ensureValue(maybeUserId, "userId");
      const operationId = ensureValue(maybeOperationId, "operationId");

      await operationsService.updateMemberPermissions(
        {
          authToken: getAuthToken(),
          userId,
          operationId,
          member: payload.member,
          permissions: payload.permissions,
        },
        catalogFlag,
      );

      return null;
    },
    {
      onMutate: () => {
        setMembersGlobalActivityAndStatus({
          activity: activities.updateMemberPermissions,
          status: "loading",
        });
      },
      onSuccess: async (_, payload) => {
        const userId = ensureValue(maybeUserId, "userId");
        if (userId === payload.member.granularId) {
          // If we have updated the current user, then invalidate the
          // current operation instead of invalidating the members.
          // The reason for this is that the permissions of the current user as a member
          // actually comes from the current operation data, not from the members list.
          await invalidateCurrentOperation();
        } else {
          // Otherwise, invalidate the members data.
          await invalidateMembers();
        }

        setMembersGlobalActivityAndStatus({
          activity: activities.updateMemberPermissions,
          status: "success",
        });
      },
      onError: () => {
        setMembersGlobalActivityAndStatus({
          activity: activities.updateMemberPermissions,
          status: "error",
        });
      },
    },
  );

  const mutate = mutateAsync;

  return useMemo(
    () => ({
      ...ensureMutationState({ data, error, status }),
      mutate,
    }),
    [data, error, status, mutate],
  );
};
